import type { PluralRuleFunction, LocaleDefinition } from '../plural-rules.types';

/**
 * Polish plural rule implementing CLDR cardinal categories.
 *
 * Maps CLDR categories to array indices for vue-i18n message variants.
 * Supports 4-5 forms: zero, one, few, many, other (optional).
 *
 * Index mapping:
 * - 0: zero (n=0, e.g., "brak pojazdów")
 * - 1: one (n=1, e.g., "1 pojazd")
 * - 2: few (n=2-4 excluding 12-14, e.g., "2 pojazdy")
 * - 3: many (n=0,5+,11-19, e.g., "5 pojazdów", "11 pojazdów")
 * - 4: other (fractions, e.g., "1.5 pojazdu", optional)
 *
 * @see https://www.unicode.org/cldr/charts/47/supplemental/language_plural_rules.html
 */
const polishPluralRule: PluralRuleFunction = (
  choice: number,
  choicesLength: number,
  orgRule?: (choice: number, choicesLength: number) => number,
): number => {
  // Only support 4 or 5 forms (CLDR categories: one, few, many, other + zero)
  if (choicesLength !== 4 && choicesLength !== 5) {
    return orgRule ? orgRule(choice, choicesLength) : 0;
  }

  // Fractions (CLDR "other"): index 4 if 5 forms, else fall to 'many' (index 3)
  if (!Number.isInteger(choice)) return choicesLength === 5 ? 4 : 3;

  // Use absolute value for all calculations (defensive against negative inputs)
  const n = Math.abs(choice);

  // Zero: index 0 (vue-i18n compatibility)
  if (n === 0) return 0;

  // One: index 1 (CLDR "one")
  if (n === 1) return 1;

  // CLDR few/many distinction
  const teen = n % 100 > 10 && n % 100 < 20;
  const lastDigit = n % 10;

  // Few: 2-4 (excluding 12-14) → index 2 (CLDR "few")
  if (!teen && lastDigit >= 2 && lastDigit <= 4) return 2;

  // Many: 5+, 11-19, etc. → index 3 (CLDR "many")
  return 3;
};

/**
 * Polish locale definition for vue-i18n.
 */
export const polishLocale: LocaleDefinition = {
  rule: polishPluralRule,
  locales: ['pl', 'pl-PL'],
};
